import React, { useEffect, useMemo, useState } from 'react';
import { AircraftType, Airstrip, ExtraServiceDefinition } from '../../types';
import { extraServicesApi } from '../../services/extraServices';

type ServicesTabProps = {
  airstrips: Airstrip[];
  aircrafts: AircraftType[];
};

const DEFAULT_FORM: Partial<ExtraServiceDefinition> = {
  code: '',
  name: '',
  description: '',
  scope: 'DESTINATION',
  pricingModel: 'FIXED',
  defaultPrice: 0,
  defaultUnit: 1,
  active: true,
  sortOrder: 0,
};

export const ServicesTab: React.FC<ServicesTabProps> = ({ airstrips, aircrafts }) => {
  const [services, setServices] = useState<ExtraServiceDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<ExtraServiceDefinition>>(DEFAULT_FORM);

  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [selectedAirstripCodes, setSelectedAirstripCodes] = useState<string[]>([]);
  const [selectedAircraftIds, setSelectedAircraftIds] = useState<string[]>([]);
  const [airstripPriceOverride, setAirstripPriceOverride] = useState<string>('');
  const [aircraftPriceOverride, setAircraftPriceOverride] = useState<string>('');
  const [editForm, setEditForm] = useState<Partial<ExtraServiceDefinition>>(DEFAULT_FORM);

  const loadServices = async () => {
    setLoading(true);
    try {
      const data = await extraServicesApi.listDefinitions(true);
      setServices(data);
      if (!selectedServiceId && data[0]?.id) {
        setSelectedServiceId(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load service definitions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  const selectedService = useMemo(
    () => services.find(item => item.id === selectedServiceId) || null,
    [services, selectedServiceId]
  );

  useEffect(() => {
    if (!selectedService) return;
    setEditForm({
      id: selectedService.id,
      code: selectedService.code,
      name: selectedService.name,
      description: selectedService.description || '',
      scope: selectedService.scope,
      pricingModel: selectedService.pricingModel,
      defaultPrice: selectedService.defaultPrice,
      defaultUnit: selectedService.defaultUnit,
      active: selectedService.active,
      sortOrder: selectedService.sortOrder,
    });
  }, [selectedService]);

  const createService = async () => {
    if (!form.code || !form.name) {
      alert('Service code and name are required.');
      return;
    }
    setSaving(true);
    try {
      await extraServicesApi.createDefinition({
        ...form,
        code: String(form.code).toUpperCase().trim(),
        name: String(form.name).trim(),
      });
      setForm(DEFAULT_FORM);
      await loadServices();
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to create service.');
    } finally {
      setSaving(false);
    }
  };

  const updateService = async () => {
    if (!selectedServiceId) return;
    if (!editForm.code || !editForm.name) {
      alert('Service code and name are required.');
      return;
    }
    setSaving(true);
    try {
      await extraServicesApi.updateDefinition(selectedServiceId, {
        ...editForm,
        code: String(editForm.code).toUpperCase().trim(),
        name: String(editForm.name).trim(),
      });
      await loadServices();
      alert('Service updated.');
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to update service.');
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async () => {
    if (!selectedServiceId) return;
    if (!confirm('Delete this service from catalog?')) return;
    setSaving(true);
    try {
      await extraServicesApi.deleteDefinition(selectedServiceId);
      const next = services.filter(s => s.id !== selectedServiceId);
      setServices(next);
      setSelectedServiceId(next[0]?.id || '');
      alert('Service deleted.');
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to delete service.');
    } finally {
      setSaving(false);
    }
  };

  const assignToAirstrips = async () => {
    if (!selectedServiceId || selectedAirstripCodes.length === 0) {
      alert('Select a service and at least one airstrip.');
      return;
    }
    setSaving(true);
    try {
      await extraServicesApi.bulkUpsertAirstripConfig({
        serviceId: selectedServiceId,
        airstripCodes: selectedAirstripCodes,
        enabled: true,
        priceOverride: airstripPriceOverride === '' ? null : Number(airstripPriceOverride),
      });
      alert('Airstrip service pricing updated.');
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to assign service to airstrips.');
    } finally {
      setSaving(false);
    }
  };

  const assignToAircraft = async () => {
    if (!selectedServiceId || selectedAircraftIds.length === 0) {
      alert('Select a service and at least one aircraft.');
      return;
    }
    setSaving(true);
    try {
      await extraServicesApi.bulkUpsertAircraftConfig({
        serviceId: selectedServiceId,
        aircraftIds: selectedAircraftIds,
        enabled: true,
        priceOverride: aircraftPriceOverride === '' ? null : Number(aircraftPriceOverride),
      });
      alert('Aircraft service pricing updated.');
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to assign service to aircraft.');
    } finally {
      setSaving(false);
    }
  };

  const toggleItem = (value: string, current: string[], setCurrent: (items: string[]) => void) => {
    if (current.includes(value)) {
      setCurrent(current.filter(item => item !== value));
      return;
    }
    setCurrent([...current, value]);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <h3 className="text-lg font-bold text-slate-900">Create Service (Catalog)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              value={form.code || ''}
              onChange={e => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              placeholder="Code (e.g. WIFI_ONB)"
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
            />
            <input
              value={form.name || ''}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Service Name"
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
            />
            <select
              value={form.scope || 'DESTINATION'}
              onChange={e => setForm(prev => ({ ...prev, scope: e.target.value as any }))}
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="ORIGIN">Origin</option>
              <option value="DESTINATION">Destination</option>
              <option value="ONBOARD">Onboard</option>
              <option value="LEG">Leg-wide</option>
              <option value="QUOTE">Quote-wide</option>
            </select>
            <select
              value={form.pricingModel || 'FIXED'}
              onChange={e => setForm(prev => ({ ...prev, pricingModel: e.target.value as any }))}
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="FIXED">Fixed</option>
              <option value="PER_PAX">Per Pax</option>
              <option value="PER_HOUR">Per Hour</option>
            </select>
            <input
              type="number"
              value={form.defaultPrice ?? 0}
              onChange={e => setForm(prev => ({ ...prev, defaultPrice: Number(e.target.value) || 0 }))}
              placeholder="Default Price"
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
            />
            <input
              type="number"
              value={form.defaultUnit ?? 1}
              onChange={e => setForm(prev => ({ ...prev, defaultUnit: Number(e.target.value) || 1 }))}
              placeholder="Default Qty"
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <textarea
            value={form.description || ''}
            onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Description"
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm min-h-[72px]"
          />
          <button
            type="button"
            onClick={createService}
            disabled={saving}
            className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Add Service'}
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <h3 className="text-lg font-bold text-slate-900">Catalog</h3>
          <div className="max-h-[320px] overflow-auto space-y-2">
            {loading && <div className="text-sm text-slate-500">Loading services...</div>}
            {!loading && services.length === 0 && <div className="text-sm text-slate-500">No services found.</div>}
            {services.map(item => (
              <label
                key={item.id}
                className={`block rounded-xl border px-3 py-2 cursor-pointer ${
                  selectedServiceId === item.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'
                }`}
              >
                <input
                  type="radio"
                  className="hidden"
                  checked={selectedServiceId === item.id}
                  onChange={() => setSelectedServiceId(item.id)}
                />
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{item.name}</div>
                    <div className="text-xs text-slate-500">{item.code} · {item.scope} · {item.pricingModel}</div>
                  </div>
                  <div className="text-xs font-semibold text-slate-700">${Number(item.defaultPrice || 0).toFixed(2)}</div>
                </div>
              </label>
            ))}
          </div>
          {selectedService && (
            <div className="rounded-xl border border-slate-200 p-3 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Edit Selected Service</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  value={editForm.code || ''}
                  onChange={e => setEditForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  value={editForm.name || ''}
                  onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <select
                  value={editForm.scope || 'DESTINATION'}
                  onChange={e => setEditForm(prev => ({ ...prev, scope: e.target.value as any }))}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="ORIGIN">Origin</option>
                  <option value="DESTINATION">Destination</option>
                  <option value="ONBOARD">Onboard</option>
                  <option value="LEG">Leg-wide</option>
                  <option value="QUOTE">Quote-wide</option>
                </select>
                <select
                  value={editForm.pricingModel || 'FIXED'}
                  onChange={e => setEditForm(prev => ({ ...prev, pricingModel: e.target.value as any }))}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="FIXED">Fixed</option>
                  <option value="PER_PAX">Per Pax</option>
                  <option value="PER_HOUR">Per Hour</option>
                </select>
                <input
                  type="number"
                  value={editForm.defaultPrice ?? 0}
                  onChange={e => setEditForm(prev => ({ ...prev, defaultPrice: Number(e.target.value) || 0 }))}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="number"
                  value={editForm.defaultUnit ?? 1}
                  onChange={e => setEditForm(prev => ({ ...prev, defaultUnit: Number(e.target.value) || 1 }))}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <textarea
                value={editForm.description || ''}
                onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[64px]"
                placeholder="Description"
              />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={editForm.active !== false}
                  onChange={e => setEditForm(prev => ({ ...prev, active: e.target.checked }))}
                />
                Active
              </label>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={deleteService}
                  disabled={saving}
                  className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 disabled:opacity-50"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={updateService}
                  disabled={saving}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <h3 className="text-lg font-bold text-slate-900">Assign to Airstrips</h3>
          <div className="text-xs text-slate-500">Selected service: {selectedService?.name || 'None'}</div>
          <input
            type="number"
            value={airstripPriceOverride}
            onChange={e => setAirstripPriceOverride(e.target.value)}
            placeholder="Price override (blank = use default)"
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
          />
          <div className="max-h-[220px] overflow-auto rounded-xl border border-slate-200 p-2 space-y-1">
            {airstrips.map(airstrip => (
              <label key={airstrip.code} className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={selectedAirstripCodes.includes(airstrip.code)}
                  onChange={() => toggleItem(airstrip.code, selectedAirstripCodes, setSelectedAirstripCodes)}
                />
                <span>{airstrip.code} - {airstrip.name}</span>
              </label>
            ))}
          </div>
          <button
            type="button"
            onClick={assignToAirstrips}
            disabled={saving}
            className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            Apply to Selected Airstrips
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <h3 className="text-lg font-bold text-slate-900">Assign to Fleet</h3>
          <div className="text-xs text-slate-500">Selected service: {selectedService?.name || 'None'}</div>
          <input
            type="number"
            value={aircraftPriceOverride}
            onChange={e => setAircraftPriceOverride(e.target.value)}
            placeholder="Price override (blank = use default)"
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
          />
          <div className="max-h-[220px] overflow-auto rounded-xl border border-slate-200 p-2 space-y-1">
            {aircrafts.map(aircraft => (
              <label key={aircraft.id} className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={selectedAircraftIds.includes(aircraft.id)}
                  onChange={() => toggleItem(aircraft.id, selectedAircraftIds, setSelectedAircraftIds)}
                />
                <span>{aircraft.tailNumber} - {aircraft.name}</span>
              </label>
            ))}
          </div>
          <button
            type="button"
            onClick={assignToAircraft}
            disabled={saving}
            className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            Apply to Selected Aircraft
          </button>
        </div>
      </div>
    </div>
  );
};
